import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Shield, CreditCard, LogOut, Check, Loader2 } from 'lucide-react';
import { Navigation } from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { trackEvent } from '@/lib/analytics';

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, profile, entitlement, updateProfile, signOut, isLoading: isAuthLoading } = useAuth();
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  // Sync form state when profile data loads or changes
  useEffect(() => {
    if (profile) {
      setUsername(profile.username || '');
      setDisplayName(profile.display_name || '');
      setAvatarUrl(profile.avatar_url || '');
      setIsPublic(profile.is_public || false);
    }
  }, [profile]);

  useEffect(() => {
    if (!isAuthLoading && !user) {
      navigate('/auth');
    }
  }, [user, isAuthLoading, navigate]);

  if (!user || !profile) {
    return null;
  }

  const hasAdsRemoved = entitlement?.remove_ads || profile?.is_verified;

  const handleSave = async () => {
    if (!username.trim()) {
      toast.error('Username is required');
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await updateProfile({
        username: username.trim(),
        display_name: displayName.trim() || null,
        avatar_url: avatarUrl.trim() || null,
        is_public: isPublic,
      });

      if (error) {
        toast.error(error.message || 'Failed to save settings');
      } else {
        toast.success('Settings saved');
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error('An unexpected error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrivacyToggle = async (checked: boolean) => {
    setIsPublic(checked);
    const { error } = await updateProfile({ is_public: checked });
    if (error) {
      toast.error('Failed to update privacy setting');
      setIsPublic(!checked); // Revert on error
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleRemoveAds = async () => {
    trackEvent.removeAdsClicked();
    setIsCheckingOut(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-payment');
      
      if (error) throw error;
      
      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setIsCheckingOut(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-xl mx-auto">
          {/* Back to Profile */}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/profile')}
            className="gap-2 mb-6 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Profile
          </Button>

          <h1 className="text-2xl font-semibold mb-8">Settings</h1>

          {/* Profile Settings */}
          <div className="game-card mb-6">
            <div className="flex items-center gap-2 mb-6">
              <User className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-medium">Profile</h2>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="username" className="text-sm">Username</Label>
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="your_username"
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label htmlFor="display-name" className="text-sm">Display Name</Label>
                <Input
                  id="display-name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your Name"
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label htmlFor="avatar" className="text-sm">Avatar URL</Label>
                <Input
                  id="avatar"
                  value={avatarUrl}
                  onChange={(e) => setAvatarUrl(e.target.value)}
                  placeholder="https://..."
                  className="mt-1.5"
                />
              </div>

              <Button onClick={handleSave} disabled={isSaving} size="sm" className="mt-2">
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </div>

          {/* Privacy Settings */}
          <div className="game-card mb-6">
            <div className="flex items-center gap-2 mb-6">
              <Shield className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-medium">Privacy</h2>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-sm">Public Profile</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Allow others to view your profile and runs
                </p>
              </div>
              <Switch
                checked={isPublic}
                onCheckedChange={handlePrivacyToggle}
              />
            </div>
          </div>

          {/* Ads Status */}
          <div className="game-card mb-6">
            <div className="flex items-center gap-2 mb-6">
              <CreditCard className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-medium">Ads</h2>
            </div>

            <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
              <div>
                <p className="font-medium text-sm">Ad Status</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {hasAdsRemoved
                    ? profile?.is_verified
                      ? 'Removed (Verified)'
                      : 'Removed (Purchased)'
                    : 'Currently enabled'}
                </p>
              </div>
              {hasAdsRemoved ? (
                <div className="flex items-center gap-2 text-success">
                  <Check className="w-4 h-4" />
                  <span className="text-sm font-medium">No ads</span>
                </div>
              ) : (
                <div className="text-right">
                  <Button onClick={handleRemoveAds} disabled={isCheckingOut} size="sm">
                    {isCheckingOut ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      'Remove Ads'
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-1">$4.99 one-time</p>
                </div>
              )}
            </div>
          </div>

          <Separator className="my-6" />

          {/* Sign Out */}
          <Button variant="outline" onClick={handleSignOut} className="gap-2">
            <LogOut className="w-4 h-4" />
            Sign Out
          </Button>
        </div>
      </main>
    </div>
  );
}
